/**
 * RotationPreview — iter230
 *
 * A 3-month "next up" strip visible to all group members on the overview tab.
 * Renders only when monthly rotation is configured and has upcoming payers.
 * Purpose: let everyone plan around who's on venue duty without opening the
 * admin config dialog.
 *
 * Props:
 *   groupId — string
 *   group   — group doc (needs `members` for name lookup)
 */
import { useEffect, useMemo, useState } from 'react';
import { RotateCw, ChevronRight } from 'lucide-react';
import api from '../lib/api';

const MONTH_LABELS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

function monthKey(offset = 0) {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() + offset);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function labelFor(key) {
  // "2026-09" → "Sep '26"
  const [y, m] = key.split('-');
  return `${MONTH_LABELS[parseInt(m, 10) - 1]} '${y.slice(2)}`;
}

export default function RotationPreview({ groupId, group }) {
  const [rotation, setRotation] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!groupId) return;
    let cancelled = false;
    api.get(`/groups/${groupId}/payment-rotation`)
      .then((r) => { if (!cancelled) setRotation(r.data); })
      .catch(() => { if (!cancelled) setError(true); });
    return () => { cancelled = true; };
  }, [groupId]);

  const upcoming = useMemo(() => {
    if (!rotation || rotation.mode !== 'monthly') return [];
    const sched = rotation.monthly_schedule || {};
    const nameById = {};
    (group?.members || []).forEach((m) => { nameById[m.id] = m.name; });
    const out = [];
    for (let i = 0; i < 6 && out.length < 3; i += 1) {
      const key = monthKey(i);
      const payerId = sched[key];
      if (payerId) {
        out.push({ key, label: labelFor(key), payer: nameById[payerId] || 'Unknown' });
      }
    }
    return out;
  }, [rotation, group]);

  if (error || !rotation || rotation.mode !== 'monthly' || upcoming.length === 0) {
    return null;
  }

  return (
    <div
      className="bg-gradient-to-r from-primary/5 to-primary/10 border border-primary/20 rounded-2xl p-3"
      data-testid="rotation-preview-strip"
    >
      <div className="flex items-center gap-2 mb-2">
        <RotateCw className="w-3.5 h-3.5 text-primary" />
        <p className="text-xs font-semibold uppercase tracking-wide text-primary">
          Venue Payer Rotation — Next Up
        </p>
      </div>
      <div className="flex items-center gap-1 flex-wrap">
        {upcoming.map((u, i) => (
          <div key={u.key} className="flex items-center gap-1">
            <div
              className="bg-background border rounded-xl px-3 py-1.5 min-w-0"
              data-testid={`rotation-preview-${u.key}`}
            >
              <p className="text-xs uppercase text-muted-foreground font-semibold leading-none">
                {u.label}
              </p>
              <p className="text-sm font-bold truncate mt-0.5">{u.payer}</p>
            </div>
            {i < upcoming.length - 1 && (
              <ChevronRight className="w-4 h-4 text-primary/40 shrink-0" />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
